import React from "react";
import { fireEvent, render, screen, within } from "@testing-library/react";
import "@testing-library/jest-dom";
import { type Components } from "react-markdown";
import QCFindingsView from "./QCFindingsView";
import { QCFinding } from "../../types/qcTypes";

// ESM-only packages the shared redline module loads; not under test here.
jest.mock("rehype-sanitize", () => ({ defaultSchema: { attributes: {} } }));
jest.mock("unist-util-visit-parents", () => ({
  SKIP: "skip",
  visitParents: jest.fn(),
}));

const finding = (over: Partial<QCFinding>): QCFinding => ({
  FindingID: 1,
  QCRunID: 9,
  SectionID: null,
  SectionTitle: "Results",
  SectionIndex: 0,
  OriginalText: "revenue grew 40%",
  SuggestedText: "revenue grew 14%",
  EditedText: null,
  StartOffset: 0,
  EndOffset: 16,
  FindingType: "inaccuracy",
  Severity: "critical",
  Explanation: "The annual report says 14%.",
  SourceDocumentID: null,
  SourceSnippet: "Revenue grew 14% year on year.",
  SourceName: "Annual report.pdf",
  Resolution: null,
  ResolvedDate: null,
  Helpful: null,
  ...over,
});

// Stands in for the markdown/HTML renderers: the redline markers they emit.
const renderMarkers = (findings: QCFinding[]) => (components: Components) => {
  const Span = components.span as React.FC<Record<string, unknown>>;
  return (
    <div>
      {findings.map((f) => (
        <React.Fragment key={f.FindingID}>
          <Span
            data-qc-finding-id={String(f.FindingID)}
            data-qc-kind="original"
          >
            {f.OriginalText}
          </Span>
          <Span
            data-qc-finding-id={String(f.FindingID)}
            data-qc-kind="actions"
          />
        </React.Fragment>
      ))}
    </div>
  );
};

const renderView = (
  findings: QCFinding[],
  handlers: Partial<React.ComponentProps<typeof QCFindingsView>> = {},
) =>
  render(
    <QCFindingsView
      findings={findings}
      onAcceptFinding={jest.fn()}
      onDeclineFinding={jest.fn()}
      onRevertFinding={jest.fn()}
      onAcceptAll={jest.fn()}
      onDeclineAll={jest.fn()}
      onRevertAll={jest.fn()}
      onSubmitFeedback={jest.fn()}
      renderContent={renderMarkers(findings)}
      {...handlers}
    />,
  );

describe("QCFindingsView", () => {
  it("summarises findings by severity in the toolbar", () => {
    renderView([
      finding({ FindingID: 1, Severity: "critical" }),
      finding({ FindingID: 2, Severity: "minor" }),
      finding({ FindingID: 3, Severity: "minor" }),
    ]);

    expect(screen.getByText("1 critical")).toBeInTheDocument();
    expect(screen.getByText("2 minor")).toBeInTheDocument();
  });

  it("names each pending finding's severity next to its actions", () => {
    renderView([finding({ Severity: "major" })]);

    expect(screen.getByText("Major")).toBeInTheDocument();
    expect(screen.getByRole("button", { name: "Accept" })).toBeInTheDocument();
    expect(screen.getByRole("button", { name: "Decline" })).toBeInTheDocument();
  });

  it("opens a finding's details from the keyboard", () => {
    renderView([finding({})]);

    const highlight = screen.getByRole("button", {
      name: "Critical finding: revenue grew 40%. Show details",
    });
    expect(highlight).toHaveAttribute("tabindex", "0");
    fireEvent.keyDown(highlight, { key: "Enter" });

    const details = screen.getByRole("dialog", { name: "Finding details" });
    expect(
      within(details).getByText("The annual report says 14%."),
    ).toBeInTheDocument();
  });

  it("doesn't double the full stop when the quoted text ends a sentence", () => {
    renderView([finding({ OriginalText: "Phase one finished in May." })]);

    expect(
      screen.getByRole("button", {
        name: "Critical finding: Phase one finished in May. Show details",
      }),
    ).toBeInTheDocument();
  });

  it("asks for feedback in the details of a resolved finding, not inline", () => {
    const onSubmitFeedback = jest.fn();
    renderView([finding({ Resolution: "accepted" })], { onSubmitFeedback });

    expect(screen.queryByText(/did this help/i)).not.toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: /Critical finding/ }));
    const details = screen.getByRole("dialog", { name: "Finding details" });
    fireEvent.click(
      within(details).getByRole("button", { name: "Yes, this was helpful" }),
    );

    expect(onSubmitFeedback).toHaveBeenCalledWith(1, true);
  });

  it("doesn't ask for feedback on a pending finding", () => {
    renderView([finding({})]);
    fireEvent.click(screen.getByRole("button", { name: /Critical finding/ }));

    expect(
      screen.queryByText("Was this finding helpful?"),
    ).not.toBeInTheDocument();
  });
});
